import React, { useState } from 'react';
import axios from 'axios';
import { getConfig } from '../../config/config-helper';
import Button from './Button';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchEventDetails, selectRequestStatus } from '../../reducers/eventsSlice';
import { useCurrentUser } from '../../hooks/useCurrentUser';

interface Props {
  eventSlug: string;
  enabled: boolean | undefined;
  token: string;
  className?: string;
}

export default function InstructorReconfigureToggle({ eventSlug, enabled, token, className = '' }: Props) {
  const dispatch = useAppDispatch();
  const { canPublish } = useCurrentUser();
  const role = canPublish ? 'admin' : 'consumer';
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { dataopsAdminAppApiEndpoint } = getConfig();
  const requestStatus = useAppSelector(selectRequestStatus);

  const handleToggle = async () => {
    setIsUpdating(true);
    setErrorMessage(null);

    try {
      const url = `${dataopsAdminAppApiEndpoint}/event_management/${eventSlug}`;

      await axios.patch(
        url,
        {
          instructor_reconfigure: !enabled,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'SSC-Role': 'admin',
          },
        },
      );

      // Refresh event data
      dispatch(fetchEventDetails({ token, role, slug: eventSlug })).catch((err: unknown) => err);
    } catch (error: any) {
      console.error('Error toggling instructor reconfigure:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to update instructor reconfigure setting');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex items-center justify-between my-2">
        <div className="flex items-center">
          <span className="text-sm text-dataops-primary-dark-blue mr-2">Instructor Reconfigure:</span>
          <span
            className={`text-sm font-medium ${
              isUpdating || requestStatus === 'loading' ? 'text-gray-700' : enabled ? 'text-green-600' : 'text-gray-700'
            }`}
          >
            {isUpdating || requestStatus === 'loading' ? 'Loading...' : enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        <div className="flex items-center">
          <Button
            size="small"
            intent={enabled ? 'danger' : 'primary'}
            className="ml-2"
            disabled={isUpdating || requestStatus === 'loading'}
            onClick={handleToggle}
          >
            {isUpdating || requestStatus === 'loading' ? 'Updating...' : enabled ? 'Disable' : 'Enable'}
          </Button>
        </div>
      </div>
      {errorMessage && <p className="text-red-600 text-xs mt-1">{errorMessage}</p>}
      <p className="text-xs text-gray-500 mt-1">
        {enabled
          ? 'Instructors can reconfigure child accounts for this event.'
          : 'Only admins can reconfigure child accounts for this event.'}
      </p>
    </div>
  );
}
